import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, LogIn, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center p-6">
    <div className="w-full max-w-lg">
      <div className="text-center mb-8">
        <div className="font-heading font-extrabold text-3xl tracking-tighter">Gett<span className="text-slate-400">Proposal</span></div>
        <div className="inline-flex items-center gap-2 border border-emerald-600/40 bg-emerald-50 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-emerald-800 mt-3">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" /> COP31 Antalya · Tedarikçi Daveti
        </div>
      </div>
      {children}
    </div>
  </div>
);

export default function InviteAcceptPage() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ contact_name: "", phone: "", website: "", password: "", password2: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setPhone = (e) => {
    const v = e.target.value.replace(/[^\d+]/g, "").replace(/(?!^)\+/g, "");
    setForm((f) => ({ ...f, phone: v }));
  };
  const phoneValid = /^\+90\d{10}$/.test(form.phone);
  const phoneError = form.phone && !phoneValid ? "Telefon +90 ile başlamalı ve 10 haneli olmalıdır (örn: +90 532 123 45 67)" : "";

  useEffect(() => {
    api.get(`/public/invites/${token}`)
      .then((r) => {
        setInvite(r.data);
        setForm((f) => ({ ...f, contact_name: r.data.contact_name || "" }));
      })
      .catch(() => setNotFound(true));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.phone && !phoneValid) return toast.error("Telefon numarası hatalı: +90 ile başlayan 10 haneli numara girin");
    if (form.password.length < 6) return toast.error("Şifre en az 6 karakter olmalıdır");
    if (form.password !== form.password2) return toast.error("Şifreler eşleşmiyor");
    setBusy(true);
    try {
      await api.post(`/public/invites/${token}/accept`, {
        contact_name: form.contact_name, phone: form.phone, website: form.website, password: form.password,
      });
      setDone(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (notFound)
    return (
      <Shell>
        <div className="border border-slate-200 bg-white p-8 text-center" data-testid="invite-not-found">
          <p className="text-sm text-slate-600">Davet bulunamadı veya kaldırılmış. Lütfen sizi davet eden kişiyle iletişime geçin.</p>
        </div>
      </Shell>
    );
  if (!invite) return <Shell><p className="text-center text-sm text-slate-500">Yükleniyor...</p></Shell>;

  if (invite.status === "approved")
    return (
      <Shell>
        <div className="border border-slate-200 bg-white p-8 text-center space-y-4" data-testid="invite-already-approved">
          <CheckCircle2 size={32} className="mx-auto text-emerald-600" />
          <p className="text-sm text-slate-600"><strong>{invite.company_name}</strong> zaten COP31 Antalya tedarikçi havuzunda. Portala giriş yapabilirsiniz.</p>
          <Link to="/login"><Button className="rounded-none bg-slate-900 text-white hover:bg-slate-700 gap-2 font-semibold transition-colors duration-200"><LogIn size={14} /> Giriş Yap</Button></Link>
        </div>
      </Shell>
    );

  if (done || invite.status === "accepted")
    return (
      <Shell>
        <div className="border border-slate-200 bg-white p-8 text-center space-y-3" data-testid="invite-accepted-pending">
          <CheckCircle2 size={32} className="mx-auto text-emerald-600" />
          <h2 className="font-heading text-2xl font-bold tracking-tight">Davetiniz COP31 Tedarikçi Havuzuna İletildi</h2>
          <p className="text-sm text-slate-600">
            Başvurunuz COP31 Antalya ekibinin onayına iletildi. Onaylandığında e-posta ile bilgilendirileceksiniz
            ve belirlediğiniz şifreyle giriş yapabileceksiniz.
          </p>
        </div>
      </Shell>
    );

  return (
    <Shell>
      <div className="border border-slate-200 bg-white p-8" data-testid="invite-accept-form-wrap">
        <h1 className="font-heading text-3xl font-extrabold tracking-tighter" data-testid="invite-company-title">{invite.company_name}</h1>
        <p className="text-sm text-slate-600 mt-3">
          <strong>COP31 Antalya</strong> organizasyonu, firmanızı GettProposal tedarikçi havuzuna davet ediyor.
          Bilgilerinizi doğrulayıp şifrenizi belirleyin; onaylandığınızda COP31 teklif taleplerini almaya başlarsınız.
        </p>
        <form onSubmit={submit} className="space-y-4 mt-6">
          <div className="space-y-1.5">
            <Label>Yetkili Adı Soyadı</Label>
            <Input value={form.contact_name} onChange={set("contact_name")} required data-testid="accept-name-input" className="rounded-none bg-white border-slate-200 h-11" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Telefon</Label>
              <Input value={form.phone} onChange={setPhone} placeholder="+90 532 123 45 67" maxLength={13} data-testid="accept-phone-input" className={`rounded-none bg-white h-11 ${phoneError ? "border-red-500 focus-visible:ring-red-500" : "border-slate-200"}`} />
              {phoneError && <p className="text-xs text-red-600" data-testid="accept-phone-error">{phoneError}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Web Sitesi</Label>
              <Input value={form.website} onChange={set("website")} placeholder="www.firma.com" data-testid="accept-website-input" className="rounded-none bg-white border-slate-200 h-11" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Şifre</Label>
              <div className="relative">
                <Input type={showPwd ? "text" : "password"} value={form.password} onChange={set("password")} required data-testid="accept-password-input" className="rounded-none bg-white border-slate-200 h-11 pr-10" />
                <button type="button" onClick={() => setShowPwd((s) => !s)} tabIndex={-1} aria-label={showPwd ? "Şifreyi gizle" : "Şifreyi göster"} data-testid="accept-password-toggle" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors duration-200">
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Şifre (Tekrar)</Label>
              <div className="relative">
                <Input type={showPwd2 ? "text" : "password"} value={form.password2} onChange={set("password2")} required data-testid="accept-password2-input" className="rounded-none bg-white border-slate-200 h-11 pr-10" />
                <button type="button" onClick={() => setShowPwd2((s) => !s)} tabIndex={-1} aria-label={showPwd2 ? "Şifreyi gizle" : "Şifreyi göster"} data-testid="accept-password2-toggle" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors duration-200">
                  {showPwd2 ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          </div>
          <Button type="submit" disabled={busy} data-testid="accept-submit-button" className="w-full rounded-none h-12 bg-slate-900 text-white hover:bg-slate-700 font-semibold transition-colors duration-200">
            {busy ? "Gönderiliyor..." : "Daveti Kabul Et"}
          </Button>
          <p className="text-xs text-slate-500">Hesabınız: <strong>{invite.email}</strong> · Bu davet bağlantısı süresizdir.</p>
        </form>
      </div>
    </Shell>
  );
}
